import { useMemo, useState } from 'react';
import { useParams, Link, useNavigate, Navigate } from 'react-router-dom';
import { Row, Col, ListGroup, Card, Button } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import Loader from '../components/Loader';
import Message from '../components/Message';
import BackToTop from '../components/BackToTop';
import DrivePerformanceChart from '../components/DrivePerformanceChart';
import { useGetDriveByIdQuery, useGetDriveBenchmarksQuery, useDeleteDriveMutation } from '../slices/drivesApiSlice';
import { usePalette } from '../hooks/usePalette';
import { useListScope, getListScope, SCOPE_ALL } from '../hooks/useListScope';
import { toast } from 'react-toastify';

const ATTACHMENT_ORDER = ['native', 'usb'];
const ATTACHMENT_LABELS = { native: 'Native', usb: 'USB' };
const NO_STANDARD_RUNS_MESSAGE =
  'No standard runs yet. Please submit a benchmark using an unmodified default profile to help populate data.';
// Stats saved before the attachment split grouped USB and native runs together,
// so they are only split when every entry carries a known attachment.
// Not an expected use case: USB enclosures usually report their own model, so
// mixed USB/native stats under one model are rare, and Rebuild re-tags old data.
// This only guards against mislabelling pre-split stats as Native.
const hasAttachmentSplit = (profileStats) =>
  profileStats?.length > 0 && profileStats.every((p) => ATTACHMENT_ORDER.includes(p.attachment));

// scope='all'  → site-wide stats and benchmarks for the drive
// scope='mine' → stats and benchmarks from the signed-in user's claimed
//                benchmarks only; breadcrumb and links stay in My Drives
export default function DriveDetailsScreen({ scope = SCOPE_ALL }) {
  const { driveId } = useParams();
  const { userInfo } = useSelector((state) => state.auth);
  const listScope = useListScope(scope);
  const { mine } = listScope;

  const safeUserId = userInfo?._id;

  const { data: drive, isLoading, error } = useGetDriveByIdQuery({ mine, userId: safeUserId, driveId });

  const { data: bmData, isLoading: bmLoading, error: bmError } = useGetDriveBenchmarksQuery({ mine, userId: safeUserId, driveId });

  const { palette } = usePalette();

  const navigate = useNavigate();
  const [deleteDrive] = useDeleteDriveMutation();

  const [selectedProfile, setSelectedProfile] = useState(null);
  const [selectedAttachment, setSelectedAttachment] = useState(null);

  const backPath = listScope.drivesPath;

  const isSplit = hasAttachmentSplit(drive?.profileStats);

  const availableAttachments = useMemo(() => {
    if (!isSplit) return [];
    return ATTACHMENT_ORDER.filter((a) => drive.profileStats.some((p) => p.attachment === a));
  }, [drive, isSplit]);

  const activeAttachment = useMemo(() => {
    const candidates = [selectedAttachment, drive?.statsAttachment];
    return candidates.find((a) => a && availableAttachments.includes(a)) ?? availableAttachments[0] ?? null;
  }, [drive, selectedAttachment, availableAttachments]);

  const attachmentProfiles = useMemo(() => {
    const all = drive?.profileStats || [];
    return isSplit ? all.filter((p) => p.attachment === activeAttachment) : all;
  }, [drive, isSplit, activeAttachment]);

  const activeProfileStats = useMemo(() => {
    if (!drive) return null;
    if (!attachmentProfiles.length) {
      return {
        profileName: drive.statsProfile || 'All Profiles',
        count: drive.numBenchmarks || 0,
        avgReadMBps: drive.avgReadMBps,
        avgWriteMBps: drive.avgWriteMBps,
        avgReadIOPS: drive.avgReadIOPS,
        avgWriteIOPS: drive.avgWriteIOPS,
        avgReadLatency: drive.avgReadLatency,
        avgWriteLatency: drive.avgWriteLatency,
      };
    }
    return (
      attachmentProfiles.find((p) => p.profileName === selectedProfile) ??
      attachmentProfiles.find((p) => p.profileName === drive.statsProfile) ??
      attachmentProfiles[0]
    );
  }, [drive, selectedProfile, attachmentProfiles]);

  // Stats only include runs with unmodified profile settings
  const noStandardRuns = drive?.numBenchmarks > 0 && !drive?.profileStats?.length;

  // Bandwidth differs by profile and attachment, so the chart only shows
  // standard runs from the group selected in the stats card. Filtering happens
  // server-side before the result limit; Recent Benchmarks uses the unfiltered feed.
  const hasProfileGroup = attachmentProfiles.length > 0 && !!activeProfileStats;
  const {
    data: chartData,
    isFetching: chartLoading,
    error: chartError,
  } = useGetDriveBenchmarksQuery(
    {
      mine,
      userId: safeUserId,
      driveId,
      standard: true,
      profile: hasProfileGroup ? activeProfileStats.profileName : undefined,
      attachment: hasProfileGroup && isSplit ? activeAttachment : undefined,
    },
    { skip: !drive || noStandardRuns }
  );
  const chartBenchmarks = chartData?.benchmarks ?? [];

  const attachmentHandler = (attachment) => {
    setSelectedAttachment(attachment);
    setSelectedProfile(null);
  };

  const deleteHandler = async (driveId) => {
    if (
      window.confirm(
        `Are you sure you want to delete drive "${drive.model}"? This CANNOT be undone.`
      )
    ) {
      try {
        await deleteDrive({ userId: userInfo._id, driveId }).unwrap();
        toast.success(`Drive "${drive.model}" deleted`);
        navigate(backPath);
      } catch (err) {
        toast.error(err?.data?.message || err.error);
      }
    }
  };

  if (isLoading) return <Loader />;

  // Not one of the user's drives — show the public drive page instead
  if (mine && error?.status === 404) {
    return <Navigate to={getListScope(SCOPE_ALL).drivePath(driveId)} replace />;
  }

  if (error) return <Message variant='danger'>{error?.data?.message || error.error}</Message>;

  return (
    <>
      <Card className='shadow-sm card--opaque my-3'>
        <Card.Header className='fw-bold'>
          <nav aria-label='breadcrumb'>
            <ol className='breadcrumb mb-0'>
              <li className='breadcrumb-item fw-bold'>
                <Link to={backPath} className='breadcrumb-link--nav'>{listScope.drivesLabel}</Link>
              </li>
              <li className='breadcrumb-item active fw-bold' aria-current='page'>
                {drive.model}
              </li>
            </ol>
          </nav>
        </Card.Header>
      </Card>

      <Row className='g-4'>
        {/* Drive Info */}
        <Col lg={4} className='mb-4 pe-lg-4'>
          <Card className='shadow-sm card--opaque mb-4'>
            <Card.Header className='fw-bold'>Drive Information</Card.Header>
            <ListGroup variant='flush'>
              <ListGroup.Item>
                <Row>
                  <Col xs={5} className='fw-bold'>Model</Col>
                  <Col>{drive.model}</Col>
                </Row>
              </ListGroup.Item>
              {drive.capacity && (
                <ListGroup.Item>
                  <Row>
                    <Col xs={5} className='fw-bold'>Capacity</Col>
                    <Col>{drive.capacity}</Col>
                  </Row>
                </ListGroup.Item>
              )}
              {drive.type && (
                <ListGroup.Item>
                  <Row>
                    <Col xs={5} className='fw-bold'>Type</Col>
                    <Col>{drive.type}</Col>
                  </Row>
                </ListGroup.Item>
              )}
              {drive.interface && (
                <ListGroup.Item>
                  <Row>
                    <Col xs={5} className='fw-bold'>Interface</Col>
                    <Col>{drive.interface}</Col>
                  </Row>
                </ListGroup.Item>
              )}
              {drive.numBenchmarks != null && (
                <ListGroup.Item>
                  <Row>
                    <Col xs={5} className='fw-bold'>{listScope.benchmarksLabel}</Col>
                    <Col>{drive.numBenchmarks}</Col>
                  </Row>
                </ListGroup.Item>
              )}
              {drive.description && (
                <ListGroup.Item>
                  <Row>
                    <Col xs={5} className='fw-bold'>Description</Col>
                    <Col>{drive.description}</Col>
                  </Row>
                </ListGroup.Item>
              )}
              {drive.link && (
                <ListGroup.Item>
                  <Row>
                    <Col xs={5} className='fw-bold'>Link</Col>
                    <Col>
                      <a href={drive.link} target='_blank' rel='noopener noreferrer'>
                        {drive.link}
                      </a>
                    </Col>
                  </Row>
                </ListGroup.Item>
              )}
            </ListGroup>
          </Card>

          {/* Benchmark Stats */}
          <Card className='shadow-sm card--opaque'>
            <Card.Header className='fw-bold'>{mine ? 'My Benchmark Stats' : 'Benchmark Stats'}</Card.Header>
            <ListGroup variant='flush'>
              {activeAttachment && (
                <ListGroup.Item>
                  <Row className='align-items-center'>
                    <Col xs={5} className='fw-bold'>Attachment</Col>
                    <Col>
                      {availableAttachments.length > 1 ? (
                        <select
                          aria-label='Attachment'
                          className='form-select form-select--custom form-select-sm w-auto'
                          value={activeAttachment}
                          onChange={(e) => attachmentHandler(e.target.value)}
                        >
                          {availableAttachments.map((a) => (
                            <option className='option--custom' key={a} value={a}>
                              {ATTACHMENT_LABELS[a]}
                            </option>
                          ))}
                        </select>
                      ) : (
                        ATTACHMENT_LABELS[activeAttachment]
                      )}
                    </Col>
                  </Row>
                </ListGroup.Item>
              )}
              {noStandardRuns ? (
                <ListGroup.Item className='text-muted'>
                  {NO_STANDARD_RUNS_MESSAGE}
                </ListGroup.Item>
              ) : (
                <>
                  {activeProfileStats && (
                    <ListGroup.Item>
                      <Row className='align-items-center'>
                        <Col xs={5} className='fw-bold'>Profile</Col>
                        <Col>
                          {attachmentProfiles.length > 1 ? (
                            <select
                              className='form-select form-select--custom form-select-sm w-auto'
                              value={activeProfileStats.profileName}
                              onChange={(e) => setSelectedProfile(e.target.value)}
                            >
                              {attachmentProfiles.map((p) => (
                                <option className='option--custom' key={p.profileName} value={p.profileName}>
                                  {p.profileName} ({p.count})
                                </option>
                              ))}
                            </select>
                          ) : (
                            <>
                              {activeProfileStats.profileName}{' '}
                              <span className='text-muted small'>
                                ({activeProfileStats.count} benchmark{activeProfileStats.count === 1 ? '' : 's'})
                              </span>
                            </>
                          )}
                        </Col>
                      </Row>
                    </ListGroup.Item>
                  )}
                  <ListGroup.Item>
                    <Row className='text-center'>
                      <Col xs={5}></Col>
                      <Col className='fw-bold'>Read</Col>
                      <Col className='fw-bold'>Write</Col>
                    </Row>
                  </ListGroup.Item>
                  <ListGroup.Item>
                    <Row className='text-center'>
                      <Col xs={5} className='fw-bold text-start'>Bandwidth (MB/s)</Col>
                    <Col style={{ color: palette?.readSample }}>{activeProfileStats?.avgReadMBps ? Number(activeProfileStats.avgReadMBps).toFixed(2) : '--'}</Col>
                      <Col style={{ color: palette?.writeSample }}>{activeProfileStats?.avgWriteMBps ? Number(activeProfileStats.avgWriteMBps).toFixed(2) : '--'}</Col>
                    </Row>
                  </ListGroup.Item>
                  <ListGroup.Item>
                    <Row className='text-center'>
                      <Col xs={5} className='fw-bold text-start'>IOPS</Col>
                      <Col style={{ color: palette?.readSample }}>{activeProfileStats?.avgReadIOPS ? Number(activeProfileStats.avgReadIOPS).toFixed(0) : '--'}</Col>
                      <Col style={{ color: palette?.writeSample }}>{activeProfileStats?.avgWriteIOPS ? Number(activeProfileStats.avgWriteIOPS).toFixed(0) : '--'}</Col>
                    </Row>
                  </ListGroup.Item>
                  <ListGroup.Item>
                    <Row className='text-center'>
                      <Col xs={5} className='fw-bold text-start'>Latency (ms)</Col>
                      <Col style={{ color: palette?.readSample }}>{activeProfileStats?.avgReadLatency ? Number(activeProfileStats.avgReadLatency).toFixed(2) : '--'}</Col>
                      <Col style={{ color: palette?.writeSample }}>{activeProfileStats?.avgWriteLatency ? Number(activeProfileStats.avgWriteLatency).toFixed(2) : '--'}</Col>
                    </Row>
                  </ListGroup.Item>
                </>
              )}
            </ListGroup>
          </Card>
        </Col>

        {/* Performance Chart */}
        <Col lg={8} className='mb-4'>
          <Card className='shadow-sm card--opaque'>
            <Card.Header className='fw-bold'>
              Performance
              {attachmentProfiles.length > 0 && activeProfileStats && (
                <span className='fw-normal text-muted small ms-2'>
                  {activeProfileStats.profileName}
                  {activeAttachment ? ` · ${ATTACHMENT_LABELS[activeAttachment]}` : ''}
                </span>
              )}
            </Card.Header>
            <Card.Body>
              {noStandardRuns ? (
                <div className='text-muted text-center py-4'>{NO_STANDARD_RUNS_MESSAGE}</div>
              ) : chartLoading ? (
                <Loader />
              ) : chartError ? (
                <Message variant='danger'>
                  {chartError?.data?.message || 'Failed to load benchmark data'}
                </Message>
              ) : (
                <DrivePerformanceChart benchmarks={chartBenchmarks} palette={palette} />
              )}
            </Card.Body>
          </Card>

          {/* Recent Benchmarks */}
          {!bmLoading && !bmError && bmData?.benchmarks?.length > 0 && (
            <Card className='shadow-sm card--opaque mt-3'>
              <Card.Header className='fw-bold'>Recent Benchmarks</Card.Header>
              <ListGroup variant='flush'>
                {bmData.benchmarks.slice(0, 5).map((bm) => {
                  const bmPath = listScope.benchmarkPath(bm._id);
                  const dateStr = bm.date
                    ? new Date(bm.date).toLocaleDateString([], {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                      })
                    : 'Unknown date';
                  return (
                    <ListGroup.Item key={bm._id} className='list-group-item--clickable'>
                      <Link to={bmPath} className='text-decoration-none'>
                        <Row className='align-items-center gx-2'>
                          <Col xs={12} sm={5}>
                            <div className='fw-bold'>{dateStr}</div>
                            <div className='small text-muted'>{bm.profile}</div>
                          </Col>
                          <Col xs={6} sm={3} className='text-sm-center'>
                            <div className='small text-muted'>READ</div>
                            <div className='fw-bold' style={{ color: palette?.readSample || '#0d6efd' }}>
                              {bm.readMBps} <span className='fw-normal small'>MB/s</span>
                            </div>
                          </Col>
                          <Col xs={6} sm={3} className='text-sm-center'>
                            <div className='small text-muted'>WRITE</div>
                            <div className='fw-bold' style={{ color: palette?.writeSample || '#e67e22' }}>
                              {bm.writeMBps} <span className='fw-normal small'>MB/s</span>
                            </div>
                          </Col>
                        </Row>
                      </Link>
                    </ListGroup.Item>
                  );
                })}
              </ListGroup>
            </Card>
          )}
        </Col>
      </Row>

      {userInfo && userInfo.isAdmin && (
        <Card className='border-danger mt-3 shadow-sm mb-5'>
          <Card.Header className='bg-danger text-white fw-bold'>
            Admin Danger Zone
          </Card.Header>
          <Card.Body className='text-center p-4'>
            <p className='mb-4'>
              Warning: Deleting this drive is permanent and CANNOT be undone.
            </p>
            <Button
              variant='danger'
              size='lg'
              className='w-100 fw-bold py-3'
              onClick={() => deleteHandler(drive._id)}
            >
              DELETE
            </Button>
          </Card.Body>
        </Card>
      )}

      <BackToTop heroThreshold={200} />
    </>
  );
}